// AIDEV-NOTE: Pagina de relatorio de vendas — FilterBar + DataTable + ExportButton
import { useState } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import type { ReportFilter, VendasReportRow } from '@azoupvendas/shared';
import { FilterBar } from './components/FilterBar';
import { DataTable } from './components/DataTable';
import { ExportButton } from './components/ExportButton';
import { useReport } from './hooks/useReport';

const columns: GridColDef[] = [
  { field: 'vendedorId', headerName: 'Codigo', width: 100 },
  { field: 'vendedorNome', headerName: 'Vendedor', flex: 1 },
  { field: 'pedidos', headerName: 'Pedidos', width: 120, type: 'number' },
  {
    field: 'totalVendas',
    headerName: 'Total Vendas (R$)',
    width: 180,
    type: 'number',
    valueFormatter: (value: number) =>
      value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) ?? '0,00'
  }
];

const exportColumns = columns.map((c) => ({ field: c.field, headerName: String(c.headerName) }));

export function RelatorioVendas() {
  const now = new Date();
  const defaultPeriodo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [filter, setFilter] = useState<ReportFilter>({
    periodo: defaultPeriodo,
    limit: 25,
    offset: 0
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25
  });

  const { data, isLoading, error } = useReport('vendas', filter);

  const handlePaginationChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
    setFilter((prev) => ({
      ...prev,
      limit: model.pageSize,
      offset: model.page * model.pageSize
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Relatorio de Vendas
      </Typography>

      <FilterBar filter={filter} onChange={setFilter} />

      {data && data.rows.length > 0 && (
        <ExportButton
          title="Relatorio de Vendas"
          columns={exportColumns}
          rows={data.rows as unknown as Record<string, unknown>[]}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Erro ao carregar relatorio'}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTable<VendasReportRow>
          rows={data?.rows ?? []}
          columns={columns}
          total={data?.total ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          loading={isLoading}
          getRowId={(row) => row.vendedorId}
        />
      )}
    </Box>
  );
}
